import { type TeamsSchema } from "@/hooks/roster-craete/useTeamsQuery";
import { ManagerTable } from "./ManagerTable";
import { useEffect, useMemo, useRef, useState } from "react";
import { images } from "@/assets/img/imagesMap";
import { sortTeamsByName } from "@/utils/team-sort-util";
import type { GroupMemberInMonth } from "@/api/nurses";
import type { NurseResponse, NurseWithRelation } from "@/types/nurse-type";
import type { SortConfig } from "@/hooks/useSortableNurses";
import type { GradeConfigQueryResult } from "@/hooks/useGradeConfig";
import type { ShiftReturnType } from "@/hooks/roster-craete/useShiftsQuery";

type SortKey = "experience" | "grade" | "name";
type MoveNurseHandler = (
  nurseId: string,
  direction: "up" | "down",
  listKind: "active" | "inactive",
  visibleNurseIds: readonly string[]
) => void;

// 전입자(병동이동/파견 in)는 home group(nurses.group_id) flush 전이라 useNurses(현 그룹)에
// 없다. group_members(memberStatusMap)에는 있으므로, 그 달 목적 병동 명단/팀뷰에 보이도록
// 최소 필드로 합성 행을 만든다(표시 전용; 수정은 사이드프로필/재분배로).
function synthInboundNurse(
  member: GroupMemberInMonth,
  groupId: string | null
): NurseWithRelation {
  return {
    nurse_id: member.nurse_id,
    account_id: "",
    name: member.name ?? member.nurse_id,
    emp_num: 0,
    gender: "M",
    age: null,
    role: "RN",
    grade: member.as_of_grade ?? 0,
    experience: 0,
    is_head_nurse: false,
    weekly_off_weekday: null,
    allowed_shifts: [],
    preceptor_id: null,
    joining_date: null,
    resignation_date: null,
    nurse_memo: "",
    active: 1,
    team_id: member.as_of_team ?? null,
    sequence: 0,
    is_weekend_off: false,
    fixed_shift: null,
    preceptor: null,
    preceptees: [],
    group_id: groupId || "",
    level_: "일반",
    office_id: "",
    personal_off_adjustment: 0,
    enable_aide: false,
    enable_nurse_pair_preference: false,
    wanted_max_requests: null,
    resignation_reason: null,
    resignation_reason_memo: null,
  };
}

function useListTransition(signal: string | undefined, isPending: boolean) {
  const mountedRef = useRef(false);
  const completedSignalRef = useRef<string | undefined>(signal);
  const [isTransitioning, setIsTransitioning] = useState(false);

  useEffect(() => {
    if (!mountedRef.current) {
      mountedRef.current = true;
      completedSignalRef.current = signal;
      return;
    }

    if (isPending || completedSignalRef.current === signal) return;

    completedSignalRef.current = signal;
    setIsTransitioning(false);
    const frame = window.requestAnimationFrame(() => {
      setIsTransitioning(true);
    });
    const timeout = window.setTimeout(() => {
      setIsTransitioning(false);
    }, 420);

    return () => {
      window.cancelAnimationFrame(frame);
      window.clearTimeout(timeout);
    };
  }, [isPending, signal]);

  return isTransitioning;
}

function TransitionShell({
  isTransitioning,
  children,
  className,
}: {
  isTransitioning: boolean;
  children: React.ReactNode;
  className: string;
}) {
  return (
    <div className={className}>
      <style>
        {`
          @keyframes roster-list-refresh {
            from { opacity: 0; }
            to { opacity: 1; }
          }
        `}
      </style>
      <div
        className="will-change-transform"
        style={
          isTransitioning
            ? {
                animation:
                  "roster-list-refresh 360ms ease-out",
              }
            : undefined
        }
      >
        {children}
      </div>
    </div>
  );
}

export default function NurseManagementTables({
  localTeams,
  currentTeamId: _currentTeamId,
  selectedGroupId,
  sortedNurses,
  activeNurseIdForSideProfile,
  selectIdForSideprofile,
  showOnlyActive = true,
  isAddNewNurse = false,
  setIsAddNewNurse,
  viewState = "ALL",
  sortConfig,
  onSort,
  memberStatusMap,
  transitionSignal,
  isTransitionPending = false,
  selectedYear,
  selectedMonth,
  nurseCacheData,
  gradeData,
  shiftsData,
  isUseMid = false,
  groupName,
  onMoveNurse,
}: {
  localTeams: TeamsSchema;
  setLocalTeams: React.Dispatch<React.SetStateAction<TeamsSchema>>;
  currentTeamId: number | null;
  setCurrentTeamId: React.Dispatch<React.SetStateAction<number | null>>;
  selectedGroupId: string | null;
  sortedNurses: NurseWithRelation[];
  activeNurseIdForSideProfile: string | null;
  selectIdForSideprofile: (bool: boolean, nurseID: string) => void;
  showOnlyActive?: boolean;
  isAddNewNurse?: boolean;
  setIsAddNewNurse?: React.Dispatch<React.SetStateAction<boolean>>;
  viewState?: "ALL" | "TEAM";
  sortConfig?: SortConfig;
  onSort?: (key: SortKey) => void;
  memberStatusMap?: Map<string, GroupMemberInMonth>;
  transitionSignal?: string;
  isTransitionPending?: boolean;
  // 월 셀렉터 기준 연/월 (나이트 개수 인라인 편집용)
  selectedYear?: number;
  selectedMonth?: number;
  nurseCacheData?: NurseResponse[];
  gradeData?: GradeConfigQueryResult;
  shiftsData?: ShiftReturnType;
  isUseMid?: boolean;
  groupName?: string;
  onMoveNurse?: MoveNurseHandler;
}) {
  void _currentTeamId;

  const isTransitioning = useListTransition(
    transitionSignal,
    isTransitionPending
  );
  const useMonthMembership =
    showOnlyActive && !!memberStatusMap && memberStatusMap.size > 0;

  const { allNursesWithRelations, unassignedNurses, nursesByTeamId } =
    useMemo(() => {
      const all: NurseWithRelation[] = [];
      const unassigned: NurseWithRelation[] = [];
      const byTeamId = new Map<number, NurseWithRelation[]>();

      sortedNurses.forEach((nurse) => {
        if (showOnlyActive ? nurse.active !== 1 : nurse.active !== 0) return;

        const member = useMonthMembership
          ? memberStatusMap!.get(nurse.nurse_id)
          : undefined;

        if (useMonthMembership && !member) return;

        all.push(nurse);

        // 팀 소속 = 시점 팀(as_of_team=period), 없으면 캐시 폴백. N전담도 직접 배정한 팀 표시.
        const teamId = member?.as_of_team ?? nurse.team_id;
        if (!teamId) {
          unassigned.push(nurse);
          return;
        }

        const teamNurses = byTeamId.get(teamId);
        if (teamNurses) {
          teamNurses.push(nurse);
        } else {
          byTeamId.set(teamId, [nurse]);
        }
      });

      // 전입자(useNurses 에 없는 group_members 멤버) 합성 행 추가 → 팀뷰/카운트 일치.
      //   완전 전출(outbound)은 group_members 가 이미 제외하므로 여기 안 옴.
      if (useMonthMembership) {
        const present = new Set(sortedNurses.map((n) => n.nurse_id));
        memberStatusMap!.forEach((member, nid) => {
          if (present.has(nid)) return; // home 으로 이미 처리됨
          const nurse = synthInboundNurse(member, selectedGroupId);
          all.push(nurse);
          const teamId = nurse.team_id;
          if (!teamId) {
            unassigned.push(nurse);
            return;
          }
          const teamNurses = byTeamId.get(teamId);
          if (teamNurses) teamNurses.push(nurse);
          else byTeamId.set(teamId, [nurse]);
        });
      }

      return {
        allNursesWithRelations: all,
        unassignedNurses: unassigned,
        nursesByTeamId: byTeamId,
      };
    }, [
      memberStatusMap,
      showOnlyActive,
      sortedNurses,
      useMonthMembership,
      selectedGroupId,
    ]);

  const sortedTeams = useMemo(() => {
    if (!localTeams) return [];

    return sortTeamsByName(localTeams);
  }, [localTeams]);

  if (viewState === "TEAM") {
    if (!showOnlyActive) {
      return (
        <TransitionShell
          className="flex flex-col"
          isTransitioning={isTransitioning}
        >
          <ManagerTable
            teamName="전체"
            selectedGroupId={selectedGroupId ?? null}
            nurseDataWithRelation={allNursesWithRelations}
            activeNurseIdForSideProfile={activeNurseIdForSideProfile ?? null}
            selectIdForSideprofile={selectIdForSideprofile}
            sortConfig={sortConfig}
            onSort={onSort}
            memberStatusMap={memberStatusMap}
            year={selectedYear}
            month={selectedMonth}
            nurseCacheData={nurseCacheData}
            gradeData={gradeData}
            shiftsData={shiftsData}
            isUseMid={isUseMid}
            groupName={groupName}
            showReorder
            isActiveList={showOnlyActive}
            onMoveNurse={onMoveNurse}
          />
        </TransitionShell>
      );
    }

    return (
      <TransitionShell
        className="flex flex-col gap-8"
        isTransitioning={isTransitioning}
      >
        {sortedTeams.map((team) => {
          const teamMembers = nursesByTeamId.get(team.team_id) ?? [];

          return (
            <div key={team.team_id} className="flex flex-col">
              <h3 className="px-10 text-lg font-bold text-white bg-sig p-2 text-center rounded-t-lg grid grid-cols-3">
                <div className="flex items-center gap-2">
                  <img src={images.icon_person} alt="" className="size-3 " />
                  {teamMembers.length}
                </div>
                <div>{team.team_name} 팀</div>
                <div></div>
              </h3>
              <ManagerTable
                teamName={team.team_name}
                selectedGroupId={selectedGroupId ?? null}
                nurseDataWithRelation={teamMembers}
                activeNurseIdForSideProfile={
                  activeNurseIdForSideProfile ?? null
                }
                selectIdForSideprofile={selectIdForSideprofile}
                sortConfig={sortConfig}
                onSort={onSort}
                memberStatusMap={memberStatusMap}
                year={selectedYear}
                month={selectedMonth}
                nurseCacheData={nurseCacheData}
                gradeData={gradeData}
                shiftsData={shiftsData}
                isUseMid={isUseMid}
                groupName={groupName}
                showReorder
                isActiveList={showOnlyActive}
                onMoveNurse={onMoveNurse}
              />
            </div>
          );
        })}

        <div className="flex flex-col">
          <h3 className="px-10 text-lg font-bold text-white bg-[#C4C4C4] p-2 text-center rounded-t-lg grid grid-cols-3">
            <div className="flex items-center gap-2">
              <img src={images.icon_person} alt="" className="size-3 " />
              {unassignedNurses.length}
            </div>
            <div>미지정</div>
            <div></div>
          </h3>
          <ManagerTable
            teamName="미지정"
            selectedGroupId={selectedGroupId ?? null}
            nurseDataWithRelation={unassignedNurses}
            activeNurseIdForSideProfile={activeNurseIdForSideProfile ?? null}
            selectIdForSideprofile={selectIdForSideprofile}
            sortConfig={sortConfig}
            onSort={onSort}
            memberStatusMap={memberStatusMap}
            year={selectedYear}
            month={selectedMonth}
            nurseCacheData={nurseCacheData}
            gradeData={gradeData}
            shiftsData={shiftsData}
            isUseMid={isUseMid}
            groupName={groupName}
            showReorder
            isActiveList={showOnlyActive}
            onMoveNurse={onMoveNurse}
          />
        </div>
      </TransitionShell>
    );
  }

  return (
    <TransitionShell
      className="flex flex-col"
      isTransitioning={isTransitioning}
    >
      <ManagerTable
        teamName="전체"
        selectedGroupId={selectedGroupId ?? null}
        nurseDataWithRelation={allNursesWithRelations}
        activeNurseIdForSideProfile={activeNurseIdForSideProfile ?? null}
        selectIdForSideprofile={selectIdForSideprofile}
        sortConfig={sortConfig}
        onSort={onSort}
        isAddNewNurse={isAddNewNurse}
        setIsAddNewNurse={setIsAddNewNurse}
        memberStatusMap={memberStatusMap}
        year={selectedYear}
        month={selectedMonth}
        enableNightBulkEdit={showOnlyActive}
        nurseCacheData={nurseCacheData}
        gradeData={gradeData}
        shiftsData={shiftsData}
        isUseMid={isUseMid}
        groupName={groupName}
        showReorder
        isActiveList={showOnlyActive}
        onMoveNurse={onMoveNurse}
      />
    </TransitionShell>
  );
}
